import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Manrope-Medium.ttf", fontWeight: "medium" },
  ],
});

const COL = {
  empId: 0.8,
  name: 1.6,
  jobTitle: 1.2,
  department: 1.0,
  division: 1.2,
  date: 1.2,
  inTime: 0.8,
  outTime: 0.8,
  normalHrs: 0.7,
  workedHrs: 0.7,
  otHrs: 0.5,
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 110,
    fontSize: 10,
    fontFamily: "Manrope",
  },
  headerSpace: {
    height: 80,
    marginBottom: 10,
  },
  header_image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  centerDiv: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 10,
  },
  mainTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 3,
  },
  smallText: {
    fontSize: 9,
    color: "grey",
    marginBottom: 6,
  },

  // ── Table ──────────────────────────────────
  tableHeader: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    backgroundColor: "#2663EB",
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#ffffff",
  },

  // Outer row per employee
  employeeRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    alignItems: "flex-start",
    paddingHorizontal: "6px",
  },

  // Common fields (left side)
  commonFields: {
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingVertical: 4,
    paddingHorizontal: 6,
    // borderRightWidth: 1,
    borderRightColor: "#eee",
  },
  cell: {
    fontSize: 8,
    paddingVertical: 1,
    paddingHorizontal: "4px",
  },
  cellBold: {
    fontSize: 8,
    fontWeight: "bold",
    paddingVertical: 1,
  },

  // Punch rows (right side)
  punchesColumn: {
    flex: 1,
    flexDirection: "column",
  },
  punchRow: {
    flexDirection: "row",
    // borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  punchCell: {
    fontSize: 8,
  },

  // ── Footer ─────────────────────────────────
  footerSpace: {
    position: "absolute",
    bottom: 22,
    left: 0,
    right: 0,
    height: 80,
  },
  footer_image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  footerPageNum: {
    position: "absolute",
    bottom: 6,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "grey",
  },
});

const EmployeeProbationStatusReportDocument = ({
  employeeProbationStatusReportPrintData,
}) => {
  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );

  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;

  const fmt = (val, format) => {
    const d = dayjs(val);
    return d.isValid() ? d.format(format) : "-";
  };

  const columns = [
    { field: "SL NO", headerName: "Sl No" },
    { field: "EmpId", headerName: "Emp ID" },
    // { field: "AlternateID", headerName: "Alt ID" },
    // { field: "Status", headerName: "Status" },
    // { field: "Name", headerName: "First Name" },
    // { field: "MiddleName", headerName: "Middle Name" },
    // { field: "LastName", headerName: "Last Name" },
    { field: "Name", headerName: "Name" },
    { field: "DOB", headerName: "DOB" },
    { field: "Age", headerName: "Age" },
    // { field: "MaritalStatus", headerName: "Marital Status" },
    { field: "Gender", headerName: "Gender" },
    // { field: "Nationality", headerName: "Nationality" },
    { field: "JoinDate", headerName: "Joining Date" },
    { field: "JobTitle", headerName: "Title" },
    { field: "JobType", headerName: "Employment Type" },
    { field: "Department", headerName: "Department" },
    { field: "Division", headerName: "Division" },
    // { field: "Grade", headerName: "Grade" },
  ];

  const status = employeeProbationStatusReportPrintData?.[0]?.Status || "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        {/* TITLE */}
        <View style={styles.centerDiv}>
          <Text style={styles.mainTitle}>
            Employee Status Report {status ? `(${status})` : ""}
          </Text>
          <Text style={styles.smallText}>
            Total: {employeeProbationStatusReportPrintData?.length || 0} records
          </Text>
        </View>

        {/* HEADER ROW */}
        <View style={styles.tableHeader}>
          {columns.map((col, idx) => (
            <View key={idx} style={{ flex: 1 }}>
              <Text style={styles.tableHeaderCell}>{col.headerName}</Text>
            </View>
          ))}
        </View>

        {/* DATA ROWS */}
        {employeeProbationStatusReportPrintData?.map((row, index) => (
          <View key={index} style={styles.employeeRow}>
            {columns.map((col, colIndex) => {
              let value = "-";

              if (col.field === "SL NO") {
                value = index + 1;
              } else {
                value = row[col.field] ?? "-";
              }

              if (col.field === "DOB" && value !== "-") {
                value = fmt(value, "YYYY-MM-DD");
              }

              if (col.field === "JoinDate" && value !== "-") {
                value = fmt(value, "YYYY-MM-DD");
              }

              //   if (col.field === "FullName") {
              //     value =
              //       `${row.Name || ""} ${row.MiddleName || ""} ${row.LastName || ""}`
              //         .replace(/\s+/g, " ")
              //         .trim();
              //   }

              return (
                <View key={colIndex} style={{ flex: 1 }}>
                  <Text style={styles.cell}>{String(value)}</Text>
                </View>
              );
            })}
          </View>
        ))}

        {/* FOOTER */}
        <View style={styles.footerSpace} fixed>
          {footerImage && (
            <Image src={footerImage} style={styles.footer_image} />
          )}
        </View>

        <Text
          style={styles.footerPageNum}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default EmployeeProbationStatusReportDocument;
